import type { HTMLAttributes, ReactNode } from "react";

import { cn } from "@/lib/utils";

const RESERVE_GLASS =
  "border border-white/65 bg-white/42 text-[#332b32] shadow-[0_18px_50px_-26px_rgba(73,40,63,0.55),inset_0_1px_0_rgba(255,255,255,0.92)] backdrop-blur-[22px] dark:border-white/12 dark:bg-white/[0.07] dark:text-[#f7f3f0] dark:shadow-[0_18px_56px_-22px_rgba(0,0,0,0.72),inset_0_1px_0_rgba(255,255,255,0.12)]";

/**
 * Operational counterpart to ReserveCanvas. It keeps dense workspaces on the
 * same warm-paper/deep-plum atmosphere while allowing long content to scroll.
 * Explicit local tokens prevent legacy portal navy from recoloring descendants.
 */
export function ReserveWorkspace({
  children,
  className,
  contentClassName,
}: {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}) {
  return (
    <div
      data-reserve-workspace
      className={cn(
        "relative isolate min-h-svh w-full overflow-hidden bg-[#f3f0ea] text-[#332b32]",
        "[--background:#f3f0ea] [--foreground:#332b32] [--card:rgba(255,255,255,0.44)] [--card-foreground:#332b32] [--popover:#f8f5f0] [--popover-foreground:#332b32] [--muted:rgba(117,78,104,0.09)] [--muted-foreground:#796a74] [--accent:rgba(108,44,89,0.10)] [--accent-foreground:#332b32] [--primary:#6c2c59] [--primary-foreground:#f7f7f7] [--border:rgba(108,44,89,0.16)] [--input:rgba(108,44,89,0.20)] [--ring:#6c2c59]",
        "dark:bg-[#1b131b] dark:text-[#f7f3f0] dark:[--background:#1b131b] dark:[--foreground:#f7f3f0] dark:[--card:rgba(255,255,255,0.07)] dark:[--card-foreground:#f7f3f0] dark:[--popover:#261a25] dark:[--popover-foreground:#f7f3f0] dark:[--muted:rgba(255,255,255,0.07)] dark:[--muted-foreground:#b7a9b2] dark:[--accent:rgba(255,255,255,0.09)] dark:[--accent-foreground:#f7f3f0] dark:[--border:rgba(255,255,255,0.12)] dark:[--input:rgba(255,255,255,0.14)]",
        className,
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-20 bg-[radial-gradient(ellipse_78%_48%_at_52%_12%,rgba(117,78,104,0.22),transparent_70%),linear-gradient(135deg,#f8f6f1_0%,#eee9e3_48%,#f5f1ed_100%)] dark:bg-[radial-gradient(ellipse_78%_48%_at_52%_12%,rgba(154,96,137,0.28),transparent_70%),linear-gradient(135deg,#160f16_0%,#241823_48%,#160f16_100%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.18] [background-image:linear-gradient(rgba(108,44,89,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(108,44,89,0.12)_1px,transparent_1px)] [background-size:3px_3px] [mask-image:radial-gradient(ellipse_70%_34%_at_52%_12%,black,transparent_78%)] dark:opacity-[0.12]"
      />
      <div
        className={cn(
          "relative mx-auto w-full max-w-6xl space-y-7 px-4 pt-20 pb-8 sm:px-6 md:py-12 lg:px-8",
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
}

/** Dense operational grouping using the exact Reserve glass recipe. */
export function ReservePanel({
  children,
  className,
  ...props
}: {
  children?: ReactNode;
  className?: string;
} & Omit<HTMLAttributes<HTMLElement>, "children" | "className">) {
  return (
    <section
      data-reserve-panel
      className={cn("rounded-[1.5rem]", RESERVE_GLASS, className)}
      {...props}
    >
      {children}
    </section>
  );
}

/**
 * Minimum orientation for a complex operational surface: a meaningful title,
 * one line of context, and at most one immediate action.
 */
export function ReservePageHeader({
  title,
  description,
  mark,
  action,
  className,
}: {
  title: string;
  description?: ReactNode;
  mark?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn("flex flex-col gap-4 pt-1 sm:flex-row sm:items-end sm:justify-between", className)}>
      <div className="flex min-w-0 items-center gap-3.5">
        {mark ? (
          <span
            aria-hidden
            className={cn("flex size-10 shrink-0 items-center justify-center rounded-full", RESERVE_GLASS)}
          >
            {mark}
          </span>
        ) : null}
        <div className="min-w-0">
          <h1 className="font-serif text-4xl leading-none font-medium tracking-[-0.015em] text-balance sm:text-5xl">
            {title}
          </h1>
          {description ? (
            <p className="mt-2 max-w-2xl text-sm leading-6 text-[#796a74] dark:text-[#b7a9b2]">{description}</p>
          ) : null}
        </div>
      </div>
      {action ? <div className="shrink-0 sm:pb-1">{action}</div> : null}
    </header>
  );
}

export const reserveQuietButtonClass =
  "rounded-full border-white/65 bg-white/42 text-[#332b32] shadow-[0_12px_32px_-20px_rgba(73,40,63,0.5),inset_0_1px_0_rgba(255,255,255,0.92)] backdrop-blur-[18px] hover:bg-white/58 hover:text-[#332b32] focus-visible:ring-[#6c2c59]/35 dark:border-white/12 dark:bg-white/[0.07] dark:text-[#f7f3f0] dark:shadow-[0_12px_36px_-18px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.12)] dark:hover:bg-white/[0.11] dark:hover:text-white";

export const reservePrimaryButtonClass =
  "rounded-full border border-[#48333f] bg-[#6c2c59] text-[#f7f7f7] shadow-[0_14px_36px_-18px_rgba(83,34,68,0.72),inset_0_1px_0_rgba(255,255,255,0.14)] hover:bg-[#763260] hover:text-white focus-visible:ring-[#6c2c59]/35";

/** The portal's existing danger color in the reserve capsule shape; never a second saturated accent. */
export const reserveDestructiveButtonClass =
  "rounded-full border border-black/10 bg-destructive text-white shadow-[0_14px_36px_-18px_rgba(120,30,40,0.6),inset_0_1px_0_rgba(255,255,255,0.16)] hover:bg-destructive/90 focus-visible:ring-destructive/25 dark:border-white/10 dark:bg-destructive/80";

/** One option of a quiet segmented choice (language, client, view). */
export function reserveSegmentClass(active: boolean): string {
  return cn(
    "shrink-0 rounded-full px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#6c2c59]/35",
    active
      ? "border border-white/70 bg-white/70 text-[#332b32] shadow-[inset_0_1px_0_rgba(255,255,255,0.92)] dark:border-white/12 dark:bg-white/[0.11] dark:text-[#f7f3f0]"
      : "text-[#796a74] hover:bg-white/45 hover:text-[#332b32] dark:text-[#b7a9b2] dark:hover:bg-white/[0.07] dark:hover:text-[#f7f3f0]",
  );
}

/**
 * Multi-line code on a reserve surface: the CodeBlock keeps its dark mono
 * body but sits as deep-plum glass instead of a navy slab.
 */
export const reserveCodeBlockClass =
  "[&>div]:rounded-[1.25rem] [&>div]:border-white/10 [&>div]:bg-[#160f16]/95 [&>div]:shadow-[0_18px_50px_-26px_rgba(53,26,45,0.6),inset_0_1px_0_rgba(255,255,255,0.08)] [&>div]:backdrop-blur-[18px]";
